import { Upload } from 'lucide-react';

import React, { useEffect, useRef, useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import {
  GROUP_CATEGORIES,
  GroupCategory,
  TEXT_LIMITS,
} from '@splitfiesta/shared';
import { Button, Card, FormField, H3 } from '@splitfiesta/ui';

import { DatePicker } from '@/components/common/DatePicker';
import { DateRangePicker } from '@/components/common/DateRangePicker';
import { ImageUpload } from '@/components/common/ImageUpload';
import { FormTextareaAutoExpand } from '@/components/forms/FormTextareaAutoExpand';
import { PhotoFramer } from '@/components/groups/PhotoFramer';
import { LinkButton } from '@/components/ui/LinkButton';

import { focalObjectPosition } from '@/utils/focalPoint';
import { getGroupCategoryIcon } from '@/utils/groupCategoryIcons';

interface GroupBasicInfoProps {
  name: string;
  setName: (name: string) => void;
  description: string;
  setDescription: (description: string) => void;
  category: GroupCategory;
  setCategory: (category: GroupCategory) => void;
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  isLoading: boolean;
  isDeleting: boolean;
  groupId?: string;
  currentAvatarUrl?: string | null;
  /** Vertical focal point for the group photo (0=top, 0.5=center, 1=bottom). */
  imageFocalY?: number;
  /** Called when the user drags the photo to reframe it. */
  onImageFocalYChange?: (focalY: number) => void;
  onAvatarUploaded?: (avatarUrl: string) => void;
  /** Called when image is deleted in edit mode */
  onAvatarDeleted?: () => Promise<void>;
  /** Called when a file is selected in create mode (deferred upload) */
  onImageFileSelected?: (file: File) => void;
  /** Called when image file is cancelled in create mode */
  onImageFileCancelled?: () => void;
  onNameBlur?: () => void;
  mode?: 'create' | 'edit';
  /** Per-field edit permission flags (undefined = permitted). */
  canEditName?: boolean;
  canEditDescription?: boolean;
  canEditCategory?: boolean;
  canEditDates?: boolean;
  canEditImage?: boolean;
  /** Subtle hint shown beneath a locked field to explain it cannot be edited. */
  lockedHint?: string;
}

export function GroupBasicInfo({
  name,
  setName,
  description,
  setDescription,
  category,
  setCategory,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  isLoading,
  isDeleting,
  groupId,
  currentAvatarUrl,
  imageFocalY = 0.5,
  onImageFocalYChange,
  onAvatarUploaded,
  onAvatarDeleted,
  onImageFileSelected,
  onImageFileCancelled,
  onNameBlur,
  mode = 'create',
  canEditName = true,
  canEditDescription = true,
  canEditCategory = true,
  canEditDates = true,
  canEditImage = true,
  lockedHint,
}: GroupBasicInfoProps) {
  const { t } = useTranslation();
  const [showAllCategories, setShowAllCategories] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  // Local object-URL preview for a freshly-selected (not-yet-uploaded) image in
  // create mode, so the user can drag to reframe it before the group exists.
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(
    null
  );

  // Wrap the deferred-upload callbacks to also manage the local preview URL.
  const handleImageFileSelected = (file: File) => {
    setPendingPreviewUrl(URL.createObjectURL(file));
    onImageFileSelected?.(file);
  };

  const handleImageFileCancelled = () => {
    setPendingPreviewUrl(null);
    onImageFileCancelled?.();
  };

  // Remove the framed photo. In edit mode this deletes the uploaded image; in
  // create mode it discards the freshly-selected (not-yet-uploaded) file. After
  // removal `framerSrc` becomes null, so the framer hides and only the dropzone
  // remains.
  const handleImageRemove = () => {
    if (mode === 'edit') {
      void onAvatarDeleted?.();
    } else {
      handleImageFileCancelled();
    }
  };

  // Revoke the previous preview URL each time it changes and on unmount —
  // exactly one revoke per URL (the effect cleanup runs with the old value).
  useEffect(() => {
    const url = pendingPreviewUrl;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [pendingPreviewUrl]);

  // The image shown in the framer: an existing group photo (edit) or the
  // freshly-selected preview (create). Only when the user may edit the image.
  const framerSrc = canEditImage
    ? mode === 'edit'
      ? currentAvatarUrl || null
      : pendingPreviewUrl
    : null;

  const categoryOptions = GROUP_CATEGORIES.map(cat => ({
    value: cat,
    label: t(`groups:categories.${cat}`),
    icon: React.createElement(getGroupCategoryIcon(cat), {
      className: 'w-5 h-5',
    }),
  }));

  const selectedCategoryOption = categoryOptions.find(
    option => option.value === category
  );

  // Handle click outside to close category selection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        categoryRef.current &&
        !categoryRef.current.contains(event.target as Node)
      ) {
        setShowAllCategories(false);
      }
    };

    if (showAllCategories) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showAllCategories]);

  return (
    <Card>
      <div className="space-y-6">
        <H3 className="mb-4">{t('groups:sections.basicInfo')}</H3>

        <FormField
          id="name"
          type="text"
          label={t('groups:fields.groupName')}
          value={name}
          onChange={setName}
          onBlur={onNameBlur}
          placeholder={t('groups:placeholders.groupName', {
            currentYear: new Date().getFullYear(),
          })}
          required
          autoFocus
          disabled={isLoading || isDeleting || !canEditName}
          maxLength={TEXT_LIMITS.GROUP_NAME_MAX}
          autoComplete="off"
          data-testid="create-group-name-input"
        />
        {!canEditName && lockedHint && (
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            {lockedHint}
          </p>
        )}

        {/* Category Selection */}
        <div ref={categoryRef}>
          <label
            htmlFor="category"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('groups:fields.category')}{' '}
            <span className="text-error-500">*</span>
          </label>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
            {t('groups:descriptions.aiCategoryHelp')}
          </p>

          {!showAllCategories ? (
            <div>
              {/* Single category display */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <Button
                  variant="ghost"
                  size="lg"
                  type="button"
                  onClick={() => setShowAllCategories(true)}
                  disabled={isLoading || isDeleting || !canEditCategory}
                  className="flex items-center justify-start p-3 bg-[#FCFCFA] dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                  data-testid="create-group-category-select"
                >
                  <div className="text-orange-600 dark:text-orange-400 mr-3">
                    {selectedCategoryOption && (
                      <div className="w-8 h-8 flex items-center justify-center">
                        {React.cloneElement(selectedCategoryOption.icon, {
                          className: 'w-8 h-8',
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedCategoryOption?.label}
                    </span>
                  </div>
                </Button>
                <div className="flex items-center sm:justify-center">
                  <LinkButton
                    variant="primary"
                    size="base"
                    onClick={() => setShowAllCategories(true)}
                    disabled={isLoading || isDeleting || !canEditCategory}
                    className="text-orange-500 hover:text-orange-600 dark:text-orange-400 dark:hover:text-orange-300 whitespace-nowrap"
                  >
                    {t('groups:actions.wrongCategory')}
                  </LinkButton>
                </div>
              </div>
            </div>
          ) : (
            /* All categories grid */
            <div>
              <div className="flex justify-end mb-2">
                <LinkButton
                  variant="primary"
                  size="base"
                  onClick={() => setShowAllCategories(false)}
                  className="text-orange-500 hover:text-orange-600 dark:text-orange-400 dark:hover:text-orange-300"
                >
                  {t('common:actions.cancel')}
                </LinkButton>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {categoryOptions.map(option => (
                  <Button
                    key={option.value}
                    variant="ghost"
                    size="lg"
                    type="button"
                    onClick={() => {
                      setCategory(option.value as GroupCategory);
                      setShowAllCategories(false);
                      // Clear dates when switching to categories that don't use them
                      if (
                        !['trip', 'events', 'roommates', 'work'].includes(
                          option.value
                        )
                      ) {
                        setStartDate('');
                        setEndDate('');
                      }
                    }}
                    disabled={isLoading || isDeleting || !canEditCategory}
                    className={`
                    flex flex-col items-center p-3 border-2 w-full transition-all min-h-[80px]
                    ${
                      category === option.value
                        ? 'border-orange-500 dark:border-orange-400 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }
                  `}
                  >
                    {React.cloneElement(option.icon, {
                      className: 'w-6 h-6 mb-2',
                    })}
                    <span className="text-xs text-center leading-tight px-1 flex-1 flex items-center">
                      {option.label}
                    </span>
                  </Button>
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {t('groups:descriptions.selectCategory')}
              </p>
            </div>
          )}

          {/* Category-Specific Settings */}
          {category === 'trip' && (
            <div className="mt-4">
              <DateRangePicker
                id="tripDates"
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                startLabel={t('groups:categorySettings.startDate')}
                endLabel={t('groups:categorySettings.endDate')}
                disabled={isLoading || isDeleting || !canEditDates}
              />
              {!canEditDates && lockedHint && (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {lockedHint}
                </p>
              )}
            </div>
          )}

          {category === 'events' && (
            <div className="mt-4">
              <DatePicker
                id="eventDate"
                label={t('groups:categorySettings.eventDate')}
                value={startDate}
                onChange={setStartDate}
                disabled={isLoading || isDeleting || !canEditDates}
              />
              {!canEditDates && lockedHint && (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {lockedHint}
                </p>
              )}
            </div>
          )}

          {category === 'roommates' && (
            <div className="mt-4">
              <DateRangePicker
                id="leaseDates"
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                startLabel={t('groups:categorySettings.leaseStart')}
                endLabel={t('groups:categorySettings.leaseEnd')}
                disabled={isLoading || isDeleting || !canEditDates}
              />
              {!canEditDates && lockedHint && (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {lockedHint}
                </p>
              )}
            </div>
          )}

          {category === 'work' && (
            <div className="mt-4">
              <DateRangePicker
                id="projectDates"
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                startLabel={t('groups:categorySettings.projectStart')}
                endLabel={t('groups:categorySettings.projectEnd')}
                disabled={isLoading || isDeleting || !canEditDates}
              />
              {!canEditDates && lockedHint && (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {lockedHint}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Description and Group Image - Side by side on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Description */}
          <div>
            <FormTextareaAutoExpand
              id="description"
              label={t('groups:fields.description')}
              value={description}
              onChange={setDescription}
              placeholder={t('groups:placeholders.description')}
              disabled={isLoading || isDeleting || !canEditDescription}
              maxLength={TEXT_LIMITS.GROUP_DESCRIPTION_MAX}
              rows={7}
              minHeight={224}
              maxHeight={250}
              data-testid="create-group-description-input"
            />
            {!canEditDescription && lockedHint && (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                {lockedHint}
              </p>
            )}
          </div>

          {/* Group Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('groups:fields.groupImage')}
            </label>

            {/* Single image representation: when a photo is present and editable,
                the drag-to-reframe control IS the image (with its own remove
                button). The dropzone below is purely a select/"change" zone —
                its internal preview is hidden so there is no duplicate thumbnail. */}
            {framerSrc && (
              <div className="mb-3">
                <PhotoFramer
                  src={framerSrc}
                  focalY={imageFocalY}
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  onChange={onImageFocalYChange ?? (() => {})}
                  onRemove={handleImageRemove}
                />
              </div>
            )}

            {mode === 'edit' && groupId && onAvatarUploaded && canEditImage ? (
              // Edit mode: immediate upload with entityId. Cropping is skipped
              // (framing handled by PhotoFramer) and the internal preview hidden.
              <ImageUpload
                type="group"
                entityId={groupId}
                currentImageUrl={currentAvatarUrl}
                onImageUploaded={onAvatarUploaded}
                onImageDeleted={onAvatarDeleted}
                skipCrop
                hidePreview
              />
            ) : mode === 'create' && onImageFileSelected ? (
              // Create mode: deferred upload (file stored, uploaded after group
              // creation). Cropping skipped + internal preview hidden.
              <ImageUpload
                type="group"
                currentImageUrl={null}
                onImageUploaded={() => undefined}
                onFileSelected={handleImageFileSelected}
                onFileCancelled={handleImageFileCancelled}
                skipCrop
                hidePreview
              />
            ) : mode === 'edit' && !canEditImage ? (
              // Edit mode: image editing locked by permissions — show read-only preview
              <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center min-h-[120px] flex flex-col items-center justify-center opacity-60 cursor-not-allowed">
                {currentAvatarUrl ? (
                  <img
                    src={currentAvatarUrl}
                    alt=""
                    className="h-20 w-20 rounded-lg object-cover mb-2"
                    style={{ objectPosition: focalObjectPosition(imageFocalY) }}
                  />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-2">
                    <Upload className="h-7 w-7 text-gray-400 dark:text-gray-500" />
                  </div>
                )}
                {lockedHint && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {lockedHint}
                  </p>
                )}
              </div>
            ) : (
              // Fallback: show placeholder
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center min-h-[180px] flex flex-col items-center justify-center">
                <div className="h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
                  <Upload className="h-7 w-7 text-gray-400 dark:text-gray-500" />
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('groups:messages.imageUploadInfo')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
